import classNames from 'classnames';
import { ReactNode } from 'react';

/**
 * Heading tag variants. Maps directly to HTML heading elements h1–h6.
 */
export type HeadingTag = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

/**
 * Color variants for the Heading component.
 * - `default` — inherits the current theme color
 * - `black` — forces black text
 * - `white` — forces white text (use on dark backgrounds)
 * - `primary` — applies the primary brand color
 */
export type HeadingColor = 'default' | 'black' | 'white' | 'primary';

export type HeadingWeight = 'light' | 'regular' | 'medium' | 'bold';

/**
 * Heading component
 *
 * A semantic heading element with styling variants. Renders an h1–h6 tag
 * with optional color theming via CSS class names.
 *
 * @example
 * <Heading tag="h2" color="primary">Section Title</Heading>
 *
 * @param tag       - HTML heading tag to render. Defaults to `h1`.
 * @param color     - Color variant. Defaults to `default`.
 * @param className - Additional CSS class names to merge.
 * @param children  - Heading content.
 */
type HeadingProps = {
	tag?: HeadingTag;
	color?: HeadingColor;
	fontWeight?: HeadingWeight;
	className?: string;
	children: ReactNode;
};


export function Heading(props: HeadingProps) {
	const {
		className,
		tag: Tag = 'h1',
		color = 'default',
		fontWeight = 'regular',
		children
	} = props;

	const clzName = classNames('heading', color, fontWeight, className);
	
	return <Tag className={clzName}>{children}</Tag>;
}
